/*******************************************************************************
  LocationSelection.js

  Author: Advait; 

*******************************************************************************/
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import "../index.css";

//***************************************************************************
// This class handles location selection for door / display detection.
//***************************************************************************
function LocationSelection({
  onChange,
  locationList,
  initialSelectedLocations,
  detectionType,
}) {
  const [SelectedLocations, setSelectedLocations] = useState(initialSelectedLocations);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  const filteredLocations = useMemo(() => {
    if (!locationList) return [];
    return locationList.filter(
      (location) =>
        location.pretty_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !SelectedLocations.includes(location.name)
    );
  }, [locationList, searchTerm, SelectedLocations]);

  const selectedTags = useMemo(() => {
    if (!locationList) return [];
    return SelectedLocations.map((name) => {
      const tag = locationList.find((location) => location.name === name);
      return tag ? tag : { name: name, pretty_name: name };
    });
  }, [SelectedLocations, locationList]);

  const selectItem = (location) => {
    if (!SelectedLocations.includes(location.name)) {
      const newSelectedLocations = [...SelectedLocations, location.name];
      setSelectedLocations(newSelectedLocations);
      if (onChange) {
        onChange(newSelectedLocations);
      }
      setSearchTerm("");
      setIsOpen(false);
      setFocusedIndex(-1);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const deselectLocation = (locationToRemoveName) => {
    const newSelectedLocations = SelectedLocations.filter(
      (name) => name !== locationToRemoveName
    );
    setSelectedLocations(newSelectedLocations);
    if (onChange) {
      onChange(newSelectedLocations);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (isOpen && inputRef.current && dropdownMenuRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownMenuRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - inputRect.bottom;

      const style = {
        position: "fixed",
        left: inputRect.left,
        width: inputRect.width,
        zIndex: 9999,
      };

      if (spaceBelow < dropdownHeight && inputRect.top > dropdownHeight) {
        style.bottom = window.innerHeight - inputRect.top;
      } else {
        style.top = inputRect.bottom;
      }
      setDropdownStyle(style);
    }
  }, [isOpen, searchTerm]);

  const heading = `Location Selection for ${
    detectionType === "door" ? "Door" : "Display"
  } Detection`;
  const noLocationText = `No location(s) selected for ${
    detectionType === "door" ? "door" : "display"
  } detection`;

  //***************************************************************************
  // render the UI
  //***************************************************************************
  return (
    <>
      <td className="subTitleColumnAdditionalSetting" valign="top">
        <label>{heading}</label>
      </td>
      <td valign="top" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setFocusedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search and Select Locations..."
          className="locationSelectionInputField"
        />

        {isOpen && (
          <div
            ref={dropdownMenuRef}
            className="locationSelectionDropdown"
            style={dropdownStyle}
          >
            {filteredLocations.length > 0 ? (
              filteredLocations.map((location, index) => (
                <button
                  key={location.name}
                  type="button"
                  onClick={() => selectItem(location)}
                  onMouseOver={() => setFocusedIndex(index)}
                  className={`locationSelectionDropdownItem ${
                    index === focusedIndex ? "focused" : ""
                  }`}
                >
                  {location.pretty_name}
                </button>
              ))
            ) : (
              <div className="locationSelectionNoItems">
                {searchTerm ? "No matching location(s) found" : "No more locations available"}
              </div>
            )}
          </div>
        )}

        {SelectedLocations.length > 0 ? (
          <div className="selectedLocationsContainer">
            {selectedTags.map((location) => (
              <div key={location.name} className="selectedLocationsItem">
                <span>{location.pretty_name}</span>
                <button
                  onClick={() => deselectLocation(location.name)}
                  className="selectedLocationsRemoveButton"
                  aria-label={`Remove ${location.pretty_name}`}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="noSelectedLocation">
            {SelectedLocations.length === 0 ? noLocationText : ""}
          </div>
        )}
      </td>
    </>
  );
}
export default LocationSelection;
